import React, { useState, useEffect } from "react";
import {
  Box,
  Popover,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { globalSearch } from "../services/GlobalSearch";
import { IGlobalSearchResultDTO } from "../interfaces/GlobalSearch";
import { getOrderStatusStyle, formatOrderStatus } from "../util/Util";

interface GlobalSearchProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  searchValue: string;
  onResultClick?: (result: IGlobalSearchResultDTO) => void;
}

const GlobalSearch = ({ anchorEl, onClose, searchValue, onResultClick }: GlobalSearchProps) => {
  const [results, setResults] = useState<IGlobalSearchResultDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const performSearch = async () => {
      if (searchValue.trim().length >= 1) {
        try {
          setLoading(true);
          const searchResults = await globalSearch(searchValue.trim());
          setResults(searchResults);
        } catch (error) {
          console.error("Search failed:", error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
      }
    };

    const delayedSearch = setTimeout(performSearch, 300);
    return () => clearTimeout(delayedSearch);
  }, [searchValue]);

  const getResultIcon = (result: IGlobalSearchResultDTO) => {
    if (result.packageBarcode) {
      return (
        <Box
          component="img"
          src="/package.svg"
          alt="Barcode"
          sx={{ width: 20, height: 20, color: "#61708A" }}
        />
      );
    }
    return <LocalShippingOutlinedIcon />;
  };

  const handleResultClick = (result: IGlobalSearchResultDTO) => {
    if (onResultClick) {
      onResultClick(result);
    }
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl) && searchValue.length > 0}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      disableAutoFocus
      disableEnforceFocus
      sx={{ mt: 1 }}
    >
      <Box sx={{ width: 400, p: 1, maxHeight: "30vh", overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} sx={{color: "text.primary"}} />
          </Box>
        ) : results.length > 0 ? (
          <Box>
            {results.map((item, index) => {
              const statusStyle = getOrderStatusStyle(item.status);
              return (
                <React.Fragment key={`${item.packageBarcode}-${index}`}>
                  {index > 0 && <Divider/>}
                  <Box
                    onClick={() => handleResultClick(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1,
                      cursor: "pointer",
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "grey.100",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#61708A",
                      }}
                    >
                      {getResultIcon(item)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          color: "#000814",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        {item.customerName}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                        <Box
                          component="img"
                          src="/scan.svg"
                          alt="Scan"
                          sx={{ width: 12, height: 12 }}
                        />
                        <Typography
                          sx={{
                            overflow: "hidden",
                            color: "#61708A",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            fontWeight: 400,
                          }}
                        >
                          {item.packageBarcode}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: "5px",
                        background: statusStyle.bg,
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          overflow: "hidden",
                          color: statusStyle.color,
                          textOverflow: "ellipsis",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {item.status}
                      </Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        ) : searchValue.length >= 1 ? (
          <Box sx={{ py: 2, textAlign: "center" }}>
            <Typography color="text.secondary" fontSize="14px">
              No results found for "{searchValue}"
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Popover>
  );
};

export default GlobalSearch;
